import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SyncIcon from "@mui/icons-material/Sync";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../AuthContext";
import { useAlert } from "../AlertContext";
import InvoiceDialog from "./InvoiceDialog";
import {
  createWorkOrder,
  createWorkOrderOperation,
  deleteWorkOrderOperation,
  createInvoice,
  updateInvoice,
  listAppointments,
  listWorkOrderOperations,
  listWorkOrders,
  getInvoiceForWorkOrder,
  downloadInvoicePdf,
  updateWorkOrder,
  updateWorkOrderOperation,
  startTimeLog,
  stopTimeLog,
  listMechanics,
} from "../api";
import { formatCurrency, formatDateDE } from "../utils";

const STATUS_META = {
  OPEN: { label: "Offen", color: "info" },
  IN_PROGRESS: { label: "In Arbeit", color: "warning" },
  DONE: { label: "Abgeschlossen", color: "success" },
  CANCELLED: { label: "Storniert", color: "error" },
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({
  value,
  ...meta,
}));

const INVOICE_STATUS_META = {
  DRAFT: { label: "Entwurf", color: "default" },
  SENT: { label: "Gesendet", color: "info" },
  PAID: { label: "Bezahlt", color: "success" },
  CANCELLED: { label: "Storniert", color: "error" },
};

const OPERATION_STATUS_META = {
  OPEN: { label: "Offen", color: "default" },
  STARTED: { label: "Gestartet", color: "warning" },
  DONE: { label: "Fertig", color: "success" },
};

const OPERATION_STATUS_OPTIONS = Object.entries(OPERATION_STATUS_META).map(
  ([value, meta]) => ({ value, ...meta })
);

function parseMinutes(value) {
  if (value == null) return null;
  const numeric = Number.parseInt(String(value), 10);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }
  return numeric;
}

function formatMinutes(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return "0 min";
  }
  const rounded = Math.floor(minutes);
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  if (hours > 0 && mins > 0) {
    return `${hours} h ${mins} min`;
  }
  if (hours > 0) {
    return `${hours} h`;
  }
  return `${mins} min`;
}

function formatRunningDuration(startTime, now) {
  if (!startTime) return "—";
  const start = new Date(startTime);
  if (Number.isNaN(start.getTime())) return "—";
  const diffMs = Math.max(0, now - start.getTime());
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) {
    return `${hours} h ${minutes.toString().padStart(2, "0")} min`;
  }
  if (minutes > 0) {
    return `${minutes} min`;
  }
  return "unter einer Minute";
}

function normalizeTimeLog(log) {
  if (!log) return null;
  return {
    id: log.id,
    workOperationId: log.workOperationId,
    mechanicId: log.mechanicId,
    startTime: log.startTime,
    endTime: log.endTime,
    durationMin: log.durationMin != null ? Number(log.durationMin) : null,
  };
}

function getMechanicLabel(mechanicsMap, mechanicId) {
  if (!mechanicId) return "Nicht zugewiesen";
  return mechanicsMap.get(mechanicId) || mechanicId;
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function formatAppointmentSlot(appointment) {
  if (!appointment) return null;
  const { date, time } = appointment;
  if (date && time) {
    const combined = new Date(`${date}T${time}`);
    if (!Number.isNaN(combined.getTime())) {
      return combined.toLocaleString("de-DE", {
        dateStyle: "short",
        timeStyle: "short",
      });
    }
  }
  return [date, time].filter(Boolean).join(" ");
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value ?? "—"}</Typography>
    </Stack>
  );
}

function WorkOrderOperationsSection({ orderId, token, roles, userId, onShowAlert }) {
  const queryClient = useQueryClient();
  const assignedRoles = Array.isArray(roles) ? roles : roles ? [roles] : [];
  const isAdmin = assignedRoles.includes("ADMIN");
  const isDisponent = assignedRoles.includes("DISPONENT");
  const isMechanic = assignedRoles.includes("MECHANIKER");
  const canManageOperations = isAdmin || isDisponent;
  const [isAdding, setIsAdding] = useState(false);
  const [newOperation, setNewOperation] = useState({
    title: "",
    estimatedTimeMin: "60",
    status: "OPEN",
    mechanicId: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({
    title: "",
    estimatedTimeMin: "60",
    status: "OPEN",
  });
  const [timeLogs, setTimeLogs] = useState({});
  const [now, setNow] = useState(() => Date.now());

  const enabled = Boolean(orderId && token);

  const {
    data: operations = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "workOrderOperations",
      orderId,
      assignedRoles.slice().sort().join("|"),
      userId,
    ],
    queryFn: () => listWorkOrderOperations(token, orderId),
    enabled,
  });

  const { data: mechanics = [] } = useQuery({
    queryKey: ["mechanics"],
    queryFn: () => listMechanics(token),
    enabled: Boolean(token) && canManageOperations,
    staleTime: 60_000,
  });

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setIsAdding(false);
    setNewOperation({
      title: "",
      estimatedTimeMin: "60",
      status: "OPEN",
      mechanicId: "",
    });
    setEditingId(null);
    setEditDraft({
      title: "",
      estimatedTimeMin: "60",
      status: "OPEN",
    });
    setTimeLogs({});
  }, [orderId]);

  useEffect(() => {
    if (!Array.isArray(operations)) return;
    setTimeLogs((prev) => {
      const next = {};
      operations.forEach((operation) => {
        if (Array.isArray(operation.timeLogs) && operation.timeLogs.length > 0) {
          next[operation.id] = operation.timeLogs
            .map(normalizeTimeLog)
            .filter(Boolean)
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        } else if (prev[operation.id]) {
          next[operation.id] = prev[operation.id];
        } else {
          next[operation.id] = [];
        }
      });
      return next;
    });
  }, [operations]);

  const mechanicsOptions = useMemo(() => {
    if (!Array.isArray(mechanics)) return [];
    return mechanics.map((user) => ({
      value: user.id,
      label: user.username,
    }));
  }, [mechanics]);

  const mechanicsMap = useMemo(() => {
    const map = new Map();
    mechanicsOptions.forEach((option) => {
      map.set(option.value, option.label);
    });
    return map;
  }, [mechanicsOptions]);

  const activeByMechanic = useMemo(() => {
    const map = {};
    Object.values(timeLogs).forEach((logs = []) => {
      logs.forEach((log) => {
        if (!log?.mechanicId || log.endTime) return;
        map[log.mechanicId] = { operationId: log.workOperationId, logId: log.id };
      });
    });
    return map;
  }, [timeLogs]);

  const operationDurations = useMemo(() => {
    const durations = new Map();
    if (!Array.isArray(operations)) return durations;
    operations.forEach((operation) => {
      const logs = timeLogs[operation.id] || [];
      let total = 0;
      logs.forEach((log) => {
        if (!log) return;
        if (log.endTime) {
          total += Number.isFinite(log.durationMin) ? log.durationMin : 0;
        } else if (log.startTime) {
          const start = new Date(log.startTime);
          if (!Number.isNaN(start.getTime())) {
            const diffMs = Math.max(0, now - start.getTime());
            total += Math.floor(diffMs / 60000);
          }
        }
      });
      durations.set(operation.id, total);
    });
    return durations;
  }, [operations, timeLogs, now]);

  const overallTrackedMinutes = useMemo(() => {
    let total = 0;
    operationDurations.forEach((value) => {
      total += value;
    });
    return total;
  }, [operationDurations]);

  const createMutation = useMutation({
    mutationFn: (payload) => createWorkOrderOperation(token, orderId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workOrderOperations", orderId] });
      setIsAdding(false);
      setNewOperation({
        title: "",
        estimatedTimeMin: "60",
        status: "OPEN",
        mechanicId: "",
      });
    },
    onError: () => onShowAlert?.("Die Position konnte nicht angelegt werden."),
  });

  const updateMutation = useMutation({
    mutationFn: ({ operationId, payload }) =>
      updateWorkOrderOperation(token, orderId, operationId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workOrderOperations", orderId] });
      setEditingId(null);
    },
    onError: () => onShowAlert?.("Die Position konnte nicht aktualisiert werden."),
  });

  const deleteMutation = useMutation({
    mutationFn: (operationId) => deleteWorkOrderOperation(token, orderId, operationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workOrderOperations", orderId] });
    },
    onError: () => onShowAlert?.("Die Position konnte nicht gelöscht werden."),
  });

  const assignMechanicMutation = useMutation({
    mutationFn: ({ operationId, mechanicId }) =>
      updateWorkOrderOperation(token, orderId, operationId, { mechanicId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workOrderOperations", orderId] });
    },
    onError: () => onShowAlert?.("Der Mechaniker konnte nicht aktualisiert werden."),
  });

  const startMutation = useMutation({
    mutationFn: ({ operationId, mechanicId }) =>
      startTimeLog(token, {
        workOperationId: operationId,
        mechanicId,
      }),
    onSuccess: (log) => {
      const normalized = normalizeTimeLog(log);
      if (!normalized) return;
      setTimeLogs((prev) => {
        const logs = prev[normalized.workOperationId] ?? [];
        return {
          ...prev,
          [normalized.workOperationId]: [...logs.filter(Boolean), normalized],
        };
      });
    },
    onError: (error) => {
      if (error?.status === 409) {
        onShowAlert?.("Der Mechaniker hat bereits eine aktive Zeitbuchung.");
      } else if (error?.status === 403) {
        onShowAlert?.("Keine Berechtigung für diese Zeitbuchung.");
      } else {
        onShowAlert?.("Die Zeitbuchung konnte nicht gestartet werden.");
      }
    },
  });

  const stopMutation = useMutation({
    mutationFn: ({ operationId, mechanicId }) =>
      stopTimeLog(token, {
        workOperationId: operationId,
        mechanicId,
      }),
    onSuccess: (log) => {
      const normalized = normalizeTimeLog(log);
      if (!normalized) return;
      setTimeLogs((prev) => {
        const logs = prev[normalized.workOperationId] ?? [];
        const updated = logs.map((entry) =>
          entry?.id === normalized.id ? normalized : entry
        );
        const exists = updated.some((entry) => entry?.id === normalized.id);
        return {
          ...prev,
          [normalized.workOperationId]: exists ? updated : [...logs, normalized],
        };
      });
    },
    onError: (error) => {
      if (error?.status === 403) {
        onShowAlert?.("Keine Berechtigung, diese Zeitbuchung zu stoppen.");
      } else {
        onShowAlert?.("Die Zeitbuchung konnte nicht gestoppt werden.");
      }
    },
  });

  const startEdit = (operation) => {
    if (!canManageOperations) return;
    setEditingId(operation.id);
    setEditDraft({
      title: operation.title ?? operation.description ?? "",
      estimatedTimeMin:
        operation.estimatedTimeMin != null ? String(operation.estimatedTimeMin) : "60",
      status: operation.status ?? "OPEN",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ title: "", estimatedTimeMin: "60", status: "OPEN" });
  };

  const submitEdit = () => {
    if (!canManageOperations) return;
    if (!editingId) return;
    if (!editDraft.title) {
      onShowAlert?.("Bitte einen Titel für die Position angeben.");
      return;
    }
    const minutes = parseMinutes(editDraft.estimatedTimeMin);
    if (minutes == null) {
      onShowAlert?.("Bitte eine geplante Dauer größer 0 Minuten angeben.");
      return;
    }
    const payload = {
      title: editDraft.title,
      estimatedTimeMin: minutes,
      status: editDraft.status || null,
    };
    updateMutation.mutate({ operationId: editingId, payload });
  };

  const submitCreate = () => {
    if (!canManageOperations) return;
    if (!newOperation.title) {
      onShowAlert?.("Bitte einen Titel für die neue Position angeben.");
      return;
    }
    const minutes = parseMinutes(newOperation.estimatedTimeMin);
    if (minutes == null) {
      onShowAlert?.("Bitte eine geplante Dauer größer 0 Minuten angeben.");
      return;
    }
    const payload = {
      title: newOperation.title,
      estimatedTimeMin: minutes,
      status: newOperation.status || "OPEN",
      mechanicId: newOperation.mechanicId ? newOperation.mechanicId : null,
    };
    createMutation.mutate(payload);
  };

  const handleDelete = (id) => {
    if (!canManageOperations) return;
    deleteMutation.mutate(id);
  };

  const startPendingId = startMutation.isPending ? startMutation.variables?.operationId : null;
  const stopPendingId = stopMutation.isPending ? stopMutation.variables?.operationId : null;

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Positionen</Typography>
        {canManageOperations && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => setIsAdding(true)}
            disabled={!orderId || isAdding || createMutation.isPending}
          >
            Position hinzufügen
          </Button>
        )}
      </Stack>

      {isLoading ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Typography color="error" variant="body2">
          Die Positionen konnten nicht geladen werden.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Arbeitsposition</TableCell>
              <TableCell align="right">Geplante Dauer</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell>Mechaniker</TableCell>
              <TableCell align="right">Zeitsteuerung</TableCell>
              <TableCell align="right">Erfasste Zeit</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(operations) && operations.length > 0 ? (
              operations.map((operation) => {
                const isEditing = editingId === operation.id;
                const logs = timeLogs[operation.id] ?? [];
                const activeLog = logs.find((log) => log && !log.endTime);
                const assignedMechanicId = operation.mechanicId ?? "";
                const mechanicIdForDisplay = assignedMechanicId || activeLog?.mechanicId || "";
                const effectiveMechanicId = isMechanic ? userId : mechanicIdForDisplay;
                const currentMechanicForBlocking = effectiveMechanicId || "";
                const mechanicBlocked =
                  currentMechanicForBlocking &&
                  activeByMechanic[currentMechanicForBlocking] &&
                  activeByMechanic[currentMechanicForBlocking].operationId !== operation.id;
                const blockingOperation = mechanicBlocked
                  ? operations.find(
                      (item) =>
                        item.id === activeByMechanic[currentMechanicForBlocking].operationId
                    )
                  : null;
                const totalMinutes = operationDurations.get(operation.id) ?? 0;
                const isStartPending = startPendingId === operation.id;
                const isStopPending = stopPendingId === operation.id;
                const isAssignedToCurrentMechanic =
                  !isMechanic || (assignedMechanicId && assignedMechanicId === userId);

                const handleToggle = () => {
                  if (activeLog) {
                    if (stopMutation.isPending || isStopPending) return;
                    stopMutation.mutate({
                      operationId: operation.id,
                      mechanicId: activeLog.mechanicId,
                    });
                  } else {
                    if (!effectiveMechanicId) {
                      onShowAlert?.(
                        canManageOperations
                          ? "Bitte zuerst einen Mechaniker zuweisen."
                          : "Keine Mechanikerzuordnung für diese Position."
                      );
                      return;
                    }
                    if (isMechanic && !isAssignedToCurrentMechanic) {
                      onShowAlert?.("Diese Position ist einem anderen Mechaniker zugewiesen.");
                      return;
                    }
                    if (mechanicBlocked) {
                      onShowAlert?.("Dieser Mechaniker hat bereits eine aktive Zeitbuchung.");
                      return;
                    }
                    startMutation.mutate({
                      operationId: operation.id,
                      mechanicId: effectiveMechanicId,
                    });
                  }
                };

                const startDisabled =
                  !effectiveMechanicId ||
                  (isMechanic && !isAssignedToCurrentMechanic) ||
                  mechanicBlocked ||
                  (startMutation.isPending && !isStartPending) ||
                  stopMutation.isPending ||
                  isStartPending;

                const stopDisabled =
                  !activeLog ||
                  (stopMutation.isPending && !isStopPending) ||
                  isStopPending;

                return (
                  <React.Fragment key={operation.id}>
                    <TableRow hover>
                      <TableCell sx={{ minWidth: 220 }}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            fullWidth
                            label="Titel"
                            value={editDraft.title}
                            onChange={(event) =>
                              setEditDraft((prev) => ({
                                ...prev,
                                title: event.target.value,
                              }))
                            }
                          />
                        ) : (
                          operation.title || operation.description || "—"
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 140 }}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            label="Minuten"
                            inputProps={{ min: 1, step: 1 }}
                            value={editDraft.estimatedTimeMin}
                            onChange={(event) =>
                              setEditDraft((prev) => ({
                                ...prev,
                                estimatedTimeMin: event.target.value,
                              }))
                            }
                          />
                        ) : operation.estimatedTimeMin ? (
                          formatMinutes(operation.estimatedTimeMin)
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 140 }}>
                        {isEditing ? (
                          <TextField
                            select
                            size="small"
                            label="Status"
                            value={editDraft.status}
                            onChange={(event) =>
                              setEditDraft((prev) => ({
                                ...prev,
                                status: event.target.value,
                              }))
                            }
                          >
                            {OPERATION_STATUS_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <Chip
                            label={
                              OPERATION_STATUS_META[operation.status]?.label ||
                              operation.status ||
                              "—"
                            }
                            color={
                              OPERATION_STATUS_META[operation.status]?.color || "default"
                            }
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 220 }}>
                        {canManageOperations ? (
                          <TextField
                            select
                            size="small"
                            label="Mechaniker"
                            value={assignedMechanicId}
                            onChange={(event) =>
                              assignMechanicMutation.mutate({
                                operationId: operation.id,
                                mechanicId: event.target.value || null,
                              })
                            }
                            fullWidth
                            disabled={
                              (assignMechanicMutation.isPending &&
                                assignMechanicMutation.variables?.operationId ===
                                  operation.id) ||
                              Boolean(activeLog)
                            }
                          >
                            <MenuItem value="">Nicht zugewiesen</MenuItem>
                            {mechanicsOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <Typography variant="body2">
                            {mechanicIdForDisplay
                              ? getMechanicLabel(mechanicsMap, mechanicIdForDisplay)
                              : "Nicht zugewiesen"}
                          </Typography>
                        )}
                        {Boolean(activeLog) && canManageOperations && (
                          <Typography variant="caption" color="text.secondary">
                            Stopp erforderlich, um den Mechaniker zu wechseln.
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 220 }}>
                        <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                          <Button
                            size="small"
                            variant={activeLog ? "contained" : "outlined"}
                            startIcon={
                              activeLog ? (
                                <StopIcon fontSize="small" />
                              ) : (
                                <PlayArrowIcon fontSize="small" />
                              )
                            }
                            onClick={handleToggle}
                            disabled={activeLog ? stopDisabled : startDisabled}
                          >
                            {activeLog ? "Stopp" : "Start"}
                          </Button>
                          {activeLog ? (
                            <Typography variant="caption" color="text.secondary">
                              Läuft seit {formatRunningDuration(activeLog.startTime, now)}
                            </Typography>
                          ) : mechanicBlocked && blockingOperation ? (
                            <Typography variant="caption" color="error">
                              Bereits aktiv auf „
                              {blockingOperation.title ||
                                blockingOperation.description ||
                                blockingOperation.id}
                              “
                            </Typography>
                          ) : !effectiveMechanicId ? (
                            <Typography variant="caption" color="text.secondary">
                              {canManageOperations
                                ? "Mechaniker wählen, um zu starten."
                                : "Keine Mechanikerzuordnung vorhanden."}
                            </Typography>
                          ) : isMechanic && !isAssignedToCurrentMechanic ? (
                            <Typography variant="caption" color="text.secondary">
                              Nur eigene Positionen können gestartet werden.
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 140 }}>
                        {formatMinutes(totalMinutes)}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 140 }}>
                        {canManageOperations ? (
                          isEditing ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton
                                aria-label="Speichern"
                                color="primary"
                                size="small"
                                onClick={submitEdit}
                                disabled={updateMutation.isPending}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton aria-label="Abbrechen" size="small" onClick={cancelEdit}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton
                                aria-label="Bearbeiten"
                                size="small"
                                onClick={() => startEdit(operation)}
                                disabled={updateMutation.isPending}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                aria-label="Löschen"
                                size="small"
                                onClick={() => handleDelete(operation.id)}
                                disabled={deleteMutation.isPending}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    {logs.length > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
                        >
                          <Stack spacing={1}>
                            <Typography variant="subtitle2">Zeitbuchungen</Typography>
                            {logs.map((log) => {
                              if (!log) return null;
                              const isActive = !log.endTime;
                              return (
                                <Stack
                                  key={log.id}
                                  direction={{ xs: "column", sm: "row" }}
                                  justifyContent="space-between"
                                  alignItems={{ xs: "flex-start", sm: "center" }}
                                  spacing={0.5}
                                >
                                  <Typography variant="body2">
                                    {formatDateTime(log.startTime)} – {" "}
                                    {log.endTime ? formatDateTime(log.endTime) : "läuft"}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color={isActive ? "warning.main" : "text.secondary"}
                                  >
                                    {isActive
                                      ? `Läuft seit ${formatRunningDuration(log.startTime, now)}`
                                      : formatMinutes(log.durationMin)}
                                  </Typography>
                                </Stack>
                              );
                            })}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              !isAdding && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Noch keine Positionen erfasst.
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            )}

            {isAdding && (
              <TableRow>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    label="Titel"
                    value={newOperation.title}
                    onChange={(event) =>
                      setNewOperation((prev) => ({ ...prev, title: event.target.value }))
                    }
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    label="Minuten"
                    inputProps={{ min: 1, step: 1 }}
                    value={newOperation.estimatedTimeMin}
                    onChange={(event) =>
                      setNewOperation((prev) => ({
                        ...prev,
                        estimatedTimeMin: event.target.value,
                      }))
                    }
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={newOperation.status}
                    onChange={(event) =>
                      setNewOperation((prev) => ({
                        ...prev,
                        status: event.target.value,
                      }))
                    }
                  >
                    {OPERATION_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  {canManageOperations ? (
                    <TextField
                      select
                      size="small"
                      label="Mechaniker"
                      value={newOperation.mechanicId}
                      onChange={(event) =>
                        setNewOperation((prev) => ({
                          ...prev,
                          mechanicId: event.target.value,
                        }))
                      }
                      fullWidth
                    >
                      <MenuItem value="">Nicht zugewiesen</MenuItem>
                      {mechanicsOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <Typography variant="body2">Nicht zugewiesen</Typography>
                  )}
                </TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton
                      aria-label="Speichern"
                      color="primary"
                      size="small"
                      onClick={submitCreate}
                      disabled={createMutation.isPending}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton aria-label="Abbrechen" size="small" onClick={() => setIsAdding(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {Array.isArray(operations) && operations.length > 0 && (
              <TableRow>
                <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>
                  Gesamte erfasste Zeit
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatMinutes(overallTrackedMinutes)}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

function CreateWorkOrderDialog({ open, onClose, token, appointments, appointmentsLoading, appointmentsError, onCreated }) {
  const queryClient = useQueryClient();
  const { showAlert } = useAlert();
  const [form, setForm] = useState({ customerId: "", vehicleId: "", appointmentId: "", description: "" });
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ customerId: "", vehicleId: "", appointmentId: "", description: "" });
      setTouched(false);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: (payload) => createWorkOrder(token, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["workOrders"] });
      onCreated?.(data);
    },
    onError: () => showAlert("Der Auftrag konnte nicht erstellt werden."),
    onSettled: () => {
      setTouched(false);
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    if (!form.customerId || !form.vehicleId) {
      return;
    }
    const payload = {
      customerId: form.customerId,
      vehicleId: form.vehicleId,
      appointmentId: form.appointmentId || null,
      description: form.description || null,
    };
    mutation.mutate(payload, {
      onSuccess: () => {
        setForm({ customerId: "", vehicleId: "", appointmentId: "", description: "" });
        onClose();
      },
    });
  };

  const handleAppointmentChange = (event) => {
    const value = event.target.value;
    const selected = appointments?.find((item) => item.id === value);
    setForm((prev) => ({
      ...prev,
      appointmentId: value,
      customerId: selected?.customer?.id ?? prev.customerId,
      vehicleId: selected?.vehicle?.id ?? prev.vehicleId,
      description:
        prev.description ||
        (selected
          ? [selected.service, selected.vehicle?.licensePlate ?? selected.kfz]
              .filter(Boolean)
              .join(" – ")
              .trim()
          : ""),
    }));
  };

  const disableClose = mutation.isPending;

  return (
    <Dialog open={open} onClose={disableClose ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Auftrag aus Termin erstellen</DialogTitle>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              select
              label="Termin"
              value={form.appointmentId}
              onChange={handleAppointmentChange}
              helperText="Optional – übernimmt Datum und Beschreibung aus dem Termin."
            >
              <MenuItem value="">Kein Termin verknüpft</MenuItem>
              {appointmentsLoading && <MenuItem disabled>Termine werden geladen …</MenuItem>}
              {appointmentsError && <MenuItem disabled>Termine konnten nicht geladen werden</MenuItem>}
              {appointments?.map((appointment) => (
                <MenuItem key={appointment.id} value={appointment.id}>
                  {formatAppointmentSlot(appointment)} {appointment.name ? `– ${appointment.name}` : ""}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              required
              label="Kunden-ID"
              value={form.customerId}
              error={touched && !form.customerId}
              helperText="Pflichtfeld – erwartet eine UUID."
              onChange={(event) => setForm((prev) => ({ ...prev, customerId: event.target.value }))}
            />

            <TextField
              required
              label="Fahrzeug-ID"
              value={form.vehicleId}
              error={touched && !form.vehicleId}
              helperText="Pflichtfeld – erwartet eine UUID."
              onChange={(event) => setForm((prev) => ({ ...prev, vehicleId: event.target.value }))}
            />

            <TextField
              label="Beschreibung"
              multiline
              minRows={3}
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              placeholder="Arbeiten oder Hinweise zum Auftrag"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={disableClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}>
            {mutation.isPending ? "Wird erstellt…" : "Erstellen"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default function WorkOrdersPage() {
  const { token, roles, userId, hasRole } = useContext(AuthContext);
  const assignedRoles = useMemo(
    () => (Array.isArray(roles) ? roles : roles ? [roles] : []),
    [roles],
  );
  const hasRoleOr = (roleName) =>
    (typeof hasRole === "function" && hasRole(roleName)) || assignedRoles.includes(roleName);
  const { showAlert } = useAlert();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);

  const isAdmin = hasRoleOr("ADMIN");
  const isDisponent = hasRoleOr("DISPONENT");
  const isMechanic = hasRoleOr("MECHANIKER");
  const canManageWorkOrders = isAdmin || isDisponent;
  const canManageInvoices = isAdmin || isDisponent;

  const { data: workOrders = [], isLoading: ordersLoading, error: ordersError } = useQuery({
    queryKey: [
      "workOrders",
      { status: statusFilter || "ALL", roles: assignedRoles.slice().sort(), userId },
    ],
    queryFn: () => listWorkOrders(token, { status: statusFilter || undefined }),
    enabled: Boolean(token),
    staleTime: 30_000,
  });

  const {
    data: appointments = [],
    isLoading: appointmentsLoading,
    error: appointmentsError,
  } = useQuery({
    queryKey: ["appointments"],
    queryFn: () => listAppointments(token),
    enabled: Boolean(token) && canManageWorkOrders,
    staleTime: 60_000,
  });

  const appointmentMap = useMemo(() => {
    const map = new Map();
    (appointments || []).forEach((appointment) => {
      map.set(appointment.id, appointment);
    });
    return map;
  }, [appointments]);

  const filteredOrders = useMemo(() => {
    if (!Array.isArray(workOrders)) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return workOrders;
    return workOrders.filter((order) => {
      const appointment = order.appointmentId ? appointmentMap.get(order.appointmentId) : undefined;
      const haystack = [
        order.id,
        order.description,
        order.status,
        appointment ? `${appointment.date} ${appointment.time}` : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [workOrders, searchTerm, appointmentMap]);

  useEffect(() => {
    if (!filteredOrders.length) {
      setSelectedOrderId(null);
      return;
    }
    if (!selectedOrderId || !filteredOrders.some((order) => order.id === selectedOrderId)) {
      setSelectedOrderId(filteredOrders[0].id);
    }
  }, [filteredOrders, selectedOrderId]);

  useEffect(() => {
    setInvoiceDialogOpen(false);
    setOverrideDialogOpen(false);
  }, [selectedOrderId]);

  const selectedOrder = filteredOrders.find((order) => order.id === selectedOrderId) || null;
  const selectedAppointment = selectedOrder?.appointmentId
    ? appointmentMap.get(selectedOrder.appointmentId)
    : null;

  const {
    data: invoice,
    isLoading: invoiceLoading,
    isFetching: invoiceFetching,
  } = useQuery({
    queryKey: ["invoice", selectedOrderId],
    queryFn: () => getInvoiceForWorkOrder(token, selectedOrderId),
    enabled: Boolean(token && selectedOrderId && canManageInvoices),
    staleTime: 10_000,
    keepPreviousData: true,
  });

  const closeStatusMenu = () => setStatusMenuAnchor(null);

  const statusMutation = useMutation({
    mutationFn: ({ orderId, status }) => updateWorkOrder(token, orderId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workOrders"] });
    },
    onError: () => showAlert("Der Auftragsstatus konnte nicht geändert werden."),
    onSettled: () => closeStatusMenu(),
  });

  const handleSelectStatus = (status) => {
    if (!selectedOrder || !canManageWorkOrders) return;
    statusMutation.mutate({ orderId: selectedOrder.id, status });
  };

  const handleOrderCreated = (order) => {
    if (order?.id) {
      setSelectedOrderId(order.id);
      queryClient.invalidateQueries({ queryKey: ["workOrders"] });
    }
  };

  const invoiceMutation = useMutation({
    mutationFn: ({ payload, invoiceId, mode }) => {
      if (!token) throw new Error("unauthorized");
      if (mode === "update" && invoiceId) {
        return updateInvoice(token, invoiceId, payload);
      }
      return createInvoice(token, payload);
    },
    onSuccess: (_, { mode }) => {
      showAlert(mode === "update" ? "Rechnung aktualisiert." : "Rechnung erstellt.");
      setInvoiceDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["invoice", selectedOrderId] });
    },
    onError: (error) => {
      if (error?.status === 409) {
        showAlert("Die Rechnungsnummer wird bereits verwendet.");
      } else if (error?.status === 400) {
        showAlert("Bitte prüfen Sie die Rechnungsdaten.");
      } else if (error?.status === 404) {
        showAlert("Der Auftrag wurde nicht gefunden.");
      } else {
        showAlert("Die Rechnung konnte nicht gespeichert werden.");
      }
    },
  });

  const downloadMutation = useMutation({
    mutationFn: (invoiceId) => downloadInvoicePdf(token, invoiceId),
    onSuccess: ({ blob, fileName }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "rechnung.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: () => showAlert("Das PDF konnte nicht heruntergeladen werden."),
  });

  const handleInvoiceSubmit = (form) => {
    if (!selectedOrder) return;
    const normalizedPositions = form.positions.map((position) => ({
      description: position.description,
      quantity: Number(position.quantity),
      unitPrice: Number(position.unitPrice),
      taxPercent: Number(position.taxPercent),
    }));
    invoiceMutation.mutate({
      payload: {
        number: form.number,
        date: form.date,
        workOrderId: selectedOrder.id,
        positions: normalizedPositions,
      },
      invoiceId: invoice?.id ?? null,
      mode: invoice?.id ? "update" : "create",
    });
  };

  const handleInvoiceButtonClick = () => {
    if (!selectedOrder) return;
    if (invoice?.id) {
      setOverrideDialogOpen(true);
    } else {
      setInvoiceDialogOpen(true);
    }
  };

  const handleConfirmOverride = () => {
    setOverrideDialogOpen(false);
    setInvoiceDialogOpen(true);
  };

  const handleCancelOverride = () => {
    if (!invoiceMutation.isPending) {
      setOverrideDialogOpen(false);
    }
  };

  const invoiceStatusMeta = invoice
    ? INVOICE_STATUS_META[invoice.status] ?? { label: invoice.status, color: "default" }
    : null;
  const invoiceButtonDisabled =
    invoiceLoading || invoiceFetching || invoiceMutation.isPending || !selectedOrder;

  return (
    <Container sx={{ py: 4 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        mb={3}
      >
        <div>
          <Typography variant="h4" gutterBottom>
            Aufträge
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Verwalten Sie laufende Werkstattaufträge, passen Sie Status und Positionen in Echtzeit an.
          </Typography>
        </div>
        {canManageWorkOrders && (
          <Button
            variant="contained"
            startIcon={<CalendarMonthIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Aus Termin erstellen
          </Button>
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
            <Stack spacing={2} mb={2}>
              <TextField
                label="Suche"
                placeholder="Nach Beschreibung, ID oder Termin suchen"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <MenuItem value="">Alle</MenuItem>
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {ordersLoading ? (
              <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress size={28} />
              </Box>
            ) : ordersError ? (
              <Typography color="error" variant="body2">
                Die Aufträge konnten nicht geladen werden.
              </Typography>
            ) : (
              <List sx={{ flexGrow: 1, overflow: "auto" }}>
                {filteredOrders.map((order) => {
                  const meta = STATUS_META[order.status] ?? { label: order.status, color: "default" };
                  const appointmentLabel = order.appointmentId
                    ? formatAppointmentSlot(appointmentMap.get(order.appointmentId))
                    : null;
                  return (
                    <ListItem key={order.id} disablePadding>
                      <ListItemButton
                        selected={order.id === selectedOrderId}
                        onClick={() => setSelectedOrderId(order.id)}
                        alignItems="flex-start"
                      >
                        <ListItemText
                          primary={order.description || "Ohne Beschreibung"}
                          secondary={
                            <span>
                              {formatDateTime(order.createdAt)}
                              {appointmentLabel ? ` · Termin ${appointmentLabel}` : ""}
                            </span>
                          }
                        />
                        <Chip label={meta.label} color={meta.color} size="small" sx={{ ml: 1 }} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                {filteredOrders.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                    Keine Aufträge gefunden.
                  </Typography>
                )}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          {selectedOrder ? (
            <Stack spacing={3}>
              <Paper sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
                  <Stack spacing={1}>
                    <Typography variant="h6">Auftragsdetails</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {selectedOrder.id}
                    </Typography>
                  </Stack>
                  <Chip
                    label={STATUS_META[selectedOrder.status]?.label ?? selectedOrder.status}
                    color={STATUS_META[selectedOrder.status]?.color ?? "default"}
                    sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
                  />
                </Stack>

                {canManageWorkOrders && (
                  <>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={2}>
                      <Button
                        variant="outlined"
                        startIcon={<SyncIcon />}
                        onClick={(event) => setStatusMenuAnchor(event.currentTarget)}
                        disabled={statusMutation.isPending}
                      >
                        Status ändern
                      </Button>
                    </Stack>

                    <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={closeStatusMenu}>
                      {STATUS_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.value}
                          selected={selectedOrder.status === option.value}
                          onClick={() => handleSelectStatus(option.value)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}

                <Stack spacing={1.5} mt={3}>
                  <DetailRow label="Beschreibung" value={selectedOrder.description || "Keine Beschreibung hinterlegt."} />
                  <DetailRow label="Kunden-ID" value={selectedOrder.customerId} />
                  <DetailRow label="Fahrzeug-ID" value={selectedOrder.vehicleId} />
                  <DetailRow label="Termin" value={selectedAppointment ? formatAppointmentSlot(selectedAppointment) : "Kein Termin verknüpft"} />
                  <DetailRow label="Erstellt" value={formatDateTime(selectedOrder.createdAt)} />
                  <DetailRow label="Aktualisiert" value={formatDateTime(selectedOrder.updatedAt)} />
                </Stack>

                <Divider sx={{ my: 3 }} />

                {canManageInvoices ? (
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1">Rechnung</Typography>
                      {invoiceStatusMeta && (
                        <Chip
                          label={invoiceStatusMeta.label}
                          color={invoiceStatusMeta.color}
                          size="small"
                        />
                      )}
                    </Stack>
                    {invoiceLoading && !invoice ? (
                      <Typography variant="body2" color="text.secondary">
                        Rechnung wird geladen…
                      </Typography>
                    ) : invoice ? (
                      <Stack spacing={1}>
                        <DetailRow label="Rechnungsnummer" value={invoice.number} />
                        <DetailRow label="Rechnungsdatum" value={formatDateDE(invoice.date)} />
                        <DetailRow label="Nettosumme" value={formatCurrency(invoice.netTotal)} />
                        <DetailRow label="MwSt." value={formatCurrency(invoice.taxTotal)} />
                        <DetailRow label="Bruttosumme" value={formatCurrency(invoice.grossTotal)} />
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Noch keine Rechnung erstellt.
                      </Typography>
                    )}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <Button
                        variant="contained"
                        startIcon={<ReceiptLongIcon />}
                        onClick={handleInvoiceButtonClick}
                        disabled={invoiceButtonDisabled}
                      >
                        {invoice ? "Rechnung neu generieren" : "Rechnung erstellen"}
                      </Button>
                      {invoice && (
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => downloadMutation.mutate(invoice.id)}
                          disabled={downloadMutation.isPending}
                        >
                          {downloadMutation.isPending ? "Lädt…" : "PDF herunterladen"}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Rechnungsdetails sind nur für Disponent:innen und Administrator:innen verfügbar.
                  </Typography>
                )}
              </Paper>

              <WorkOrderOperationsSection
                orderId={selectedOrder.id}
                token={token}
                roles={assignedRoles}
                userId={userId}
                onShowAlert={showAlert}
              />
            </Stack>
          ) : (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6">Kein Auftrag ausgewählt</Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Wählen Sie links einen Auftrag aus, um Details und Positionen zu bearbeiten.
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      <CreateWorkOrderDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        token={token}
        appointments={appointments}
        appointmentsLoading={appointmentsLoading}
        appointmentsError={appointmentsError}
        onCreated={handleOrderCreated}
      />

      <InvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        onSubmit={handleInvoiceSubmit}
        initialInvoice={invoice}
        order={selectedOrder}
        isSubmitting={invoiceMutation.isPending}
      />

      <Dialog
        open={overrideDialogOpen}
        onClose={invoiceMutation.isPending ? undefined : handleCancelOverride}
      >
        <DialogTitle>Vorhandene Rechnung überschreiben?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Für diesen Auftrag wurde bereits eine Rechnung erstellt. Möchten Sie sie neu generieren und die
            bestehende Version ersetzen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOverride} disabled={invoiceMutation.isPending}>
            Abbrechen
          </Button>
          <Button variant="contained" onClick={handleConfirmOverride} disabled={invoiceMutation.isPending}>
            Überschreiben
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
